import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import server from '../server';
import { CART_STORAGE_KEY } from '../const';

export type CartItem = {
  id: string | number;
  attributes: {
    // user?: { data?: { id?: number } };
    setmenu: { data: { id?: number; attributes?: any } };
    shop?: { data?: { id?: number; attributes?: any } };
    booking_date?: string;
    booking_time?: string;
    quantity?: number;
    remark?: string;
    dynamic_price?: number;
    dynamic_price_id?: number;
  };
};

type CartContextType = {
  cartList: CartItem[];
  cartLoading: boolean;
  totalPrice: number;
  counting: number;
  cartCounting: number;
  // cartRefresh: () => <void>;
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string | number) => void;
  updateQuantity: (id: string | number, quantity: number) => void;
  saveCartToStorage: () => void;
  clearCart: () => void;
  sendCartToServer: (email: string, phone: string) => Promise<string>;
};


const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartList, setCartList] = useState<CartItem[]>(() => {
    try {
      if (typeof window === 'undefined') return [];
      const raw = sessionStorage.getItem(CART_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });
  const [cartLoading, setCartLoading] = useState(false);

  // const cartRefresh = useCallback(async () => {
  //   setCartLoading(true);
  //   try {
  //     const response = await axios.get(`${SERVER_URL}/cart-setmenu-items`, {
  //       params: { populate: ['setmenu', 'shop', 'user'] },
  //     });
  //     setCartList(response.data?.data ?? []);
  //   } catch (error) {
  //     console.error('Failed to refresh cart:', error);
  //   } finally {
  //     setCartLoading(false);
  //   }
  // }, []);

  // const addToCart = useCallback(
  //   async (item: CartItem) => {
  //     try {
  //       // await axios.post(`${SERVER_URL}/cart-setmenu-items`, { data: item.attributes });
  //       // await cartRefresh();
  //     } catch (error) {
  //       console.error('Failed to add to cart:', error);
  //       throw error;
  //     }
  //   },
  //   [cartRefresh]
  // );

  const addToCart =
    (item: CartItem) => {
      try {
        // await axios.post(`${SERVER_URL}/cart-setmenu-items`, { data: item.attributes });
        // await cartRefresh();
        setCartList(prev => [...prev, item]);
      } catch (error) {
        console.error('Failed to add to cart:', error);
        throw error;
      }
    }

  // const removeFromCart = useCallback(
  //   async (id: string | number) => {
  //     try {
  //       await axios.delete(`${SERVER_URL}/cart-setmenu-items/${id}`);
  //       await cartRefresh();
  //     } catch (error) {
  //       console.error('Failed to remove from cart:', error);
  //       throw error;
  //     }
  //   },
  //   [cartRefresh]
  // );


  const removeFromCart = (id: string | number) => {
    try {
      setCartList(prev => prev.filter(item => item.id !== id));
      sessionStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  }

  const updateQuantity = (id: string | number, quantity: number) => {
    try {
      // await axios.put(`${SERVER_URL}/cart-setmenu-items/${id}`, {
      //   data: { quantity },
      // });
      // await cartRefresh();
      setCartList(prev =>
        prev.map(item =>
          item.id === id
            ? { ...item, attributes: { ...item.attributes, quantity } }
            : item
        )
      )
      sessionStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      throw error;
    }
  }
  const saveCartToStorage = useCallback(() => {
    try {
      console.log('Saving cart to storage:', cartList);
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartList));
    } catch (error) {
      console.error('Failed to save cart to storage:', error);
    }
  }, [cartList]);
  const clearCart = useCallback(() => {
    setCartList([]);
    try {
      sessionStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear persisted cart:', error);
    }
  }, []);
  const totalPrice = cartList.reduce((acc, item) => {
    const price = item.attributes.dynamic_price ?? 0;
    const quantity = item.attributes.quantity ?? 0;
    return acc + price * quantity;
  }, 0);

  const counting = cartList.reduce((acc, item) => acc + (item.attributes.quantity ?? 0), 0);
  const cartCounting = cartList.length;
  const sendCartToServer = async (email: string, phone: string) => {
    setCartLoading(true);
    const session_id = crypto.randomUUID();
    try {

      for (const item of cartList) {
        const setmenuId = item.attributes.setmenu?.data?.id;
        if (!setmenuId) throw new Error("Missing setmenu id");

        const cartData = {
          session_id,
          setmenu: { id: setmenuId },
          quantity: item.attributes.quantity ?? 1,
          booking_date: item.attributes.booking_date,
          booking_time: item.attributes.booking_time,
          shop: item.attributes.shop?.data?.id,
          remark: item.attributes.remark,
          email,
          phone
        };

        const result = await server.post("cart-campaign-items", {
          data: cartData,
        });
        console.log({ result })
      }


    } catch (error) {
      console.error('Failed to send cart to server:', error);
      throw error;
    } finally {
      setCartLoading(false);
      return session_id;
    }
  }
  return (
    <CartContext.Provider
      value={{
        cartList,
        cartLoading,
        totalPrice,
        counting,
        cartCounting,
        addToCart,
        removeFromCart,
        updateQuantity,
        saveCartToStorage,
        clearCart,
        sendCartToServer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within CartProvider');
  }
  return context;
}